import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Select, SelectOption } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { Tender, TenderStatus } from "@/pages/Tenders/types";
import { Badge } from "@/components/Badge";

interface TenderStatusModalProps {
    isOpen: boolean;
    tender: Tender | null;
    onClose: () => void;
    onSubmit: (
        targetStatus: TenderStatus,
        reason: string | null
    ) => Promise<void>;
    isSubmitting?: boolean;
}

export const TenderStatusModal: React.FC<TenderStatusModalProps> = ({
    isOpen,
    tender,
    onClose,
    onSubmit,
    isSubmitting = false,
}) => {
    const { t } = useTranslation("global");

    const [targetStatus, setTargetStatus] = useState<TenderStatus | "">("");
    const [reason, setReason] = useState<string>("");

    useEffect(() => {
        if (tender) {
            const currentTag =
                typeof tender.status === "object"
                    ? tender.status?.tag
                    : tender.status;
            let defaultNext: TenderStatus | "" = "";
            if (currentTag === "decision_pending")
                defaultNext = "study_pending";
            if (currentTag === "study_pending") defaultNext = "accepted";

            setTargetStatus(defaultNext);
            setReason(tender.cancellation_reason || "");
        }
    }, [tender, isOpen]);

    if (!tender) {
        return null;
    }

    const currentTag =
        typeof tender.status === "object" ? tender.status?.tag : tender.status;

    const allStatusOptions: SelectOption<TenderStatus | "">[] = [
        {
            value: "decision_pending",
            label: t("tender.status.decision_pending"),
        },
        { value: "study_pending", label: t("tender.status.study_pending") },
        { value: "accepted", label: t("tender.status.accepted") },
        { value: "rejected", label: t("tender.status.rejected") },
    ];
    const statusOptions = allStatusOptions.filter(
        (option) => option.value !== currentTag
    );

    const handleSubmit = async (): Promise<void> => {
        if (!targetStatus) return;
        await onSubmit(targetStatus as TenderStatus, reason.trim() || null);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tender.status_modal_title")}
            //subtitle={tender.code}
            maxWidth="md"
            onSubmit={handleSubmit}
            submitLabel={t("tender.confirm")}
            cancelLabel={t("tender.cancel")}
            isSubmitting={isSubmitting}
            submitDisabled={!targetStatus}
        >
            <div className="space-y-4">
                {/*
                <p className="text-sm text-slate-500 dark:text-zinc-400">
                    {t('tender.status_modal_desc')}
                </p>
                */}
                <div className="text-center">
                    <Badge value={tender.code} />
                </div>

                <div className="text-center rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-dark-card">
                    {tender.title}
                </div>

                <div className="text-center rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-dark-card">
                    <p className="text-xs text-slate-500">
                        {t("tender.current_status")}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        {t(`tender.status.${currentTag}`)}
                    </p>
                </div>

                <Select<TenderStatus | "">
                    label={t("tender.target_status")}
                    value={targetStatus}
                    onChange={(val) => setTargetStatus(val)}
                    options={statusOptions}
                    full
                />

                <Textarea
                    label={t("tender.status_reason")}
                    value={reason}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setReason(e.target.value)
                    }
                    placeholder={t("tender.field_observations")}
                    resizable={true}
                    fullscreenEnabled={true}
                    rows={5}
                />
            </div>
        </Modal>
    );
};
